import * as React from 'react';
import dayjs from 'dayjs';
import { formatNumber } from '@progress/kendo-intl';
import { dateFormatter, dateWindowFormatter } from 'utils/utils';

type Props = {
  originalAllInRate: number | null;
  alternativeAllInRate: number | null;
  originalPickupScheduled: Date | string;
  originalPickupLatest: Date | string | null;
  alternativePickup: Date | string | null;
  originalDeliveryScheduled: Date | string;
  originalDeliveryLatest: Date | string | null;
  alternativeDelivery: Date | string | null;
};

const numbersEqual = (first: number | null | undefined, second: number | null | undefined) => {
  if (first === null || first === undefined || second === null || second === undefined) {
    return false;
  }

  return first === second;
};

const datesEqual = (first: dayjs.ConfigType, second: dayjs.ConfigType) => {
  if (!first || !second) {
    return false;
  }

  return dayjs.utc(first).valueOf() === dayjs.utc(second).valueOf();
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '--';
  }

  return formatNumber(value, 'c');
};

const formatDateTime = (value: dayjs.ConfigType) => {
  if (!value) {
    return '--';
  }

  return dateFormatter(value);
};

const formatCurrencyDifference = (
  original: number | null | undefined,
  alternative: number | null | undefined
) => {
  if (original === null || original === undefined || alternative === null || alternative === undefined) {
    return '--';
  }

  const diff = alternative - original;
  if (diff === 0) {
    return 'No change';
  }

  const formatted = formatNumber(Math.abs(diff), 'c');
  return diff > 0 ? `+${formatted}` : `-${formatted}`;
};

const formatTimeDifference = (
  original: dayjs.ConfigType,
  alternative: dayjs.ConfigType
) => {
  if (!original || !alternative) {
    return '--';
  }

  const originalUtc = dayjs.utc(original);
  const alternativeUtc = dayjs.utc(alternative);
  const diffMinutes = alternativeUtc.diff(originalUtc, 'minute');

  if (diffMinutes === 0) {
    return 'No change';
  }

  const totalMinutes = Math.abs(diffMinutes);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  const magnitude = parts.length > 0 ? parts.join(' ') : '0m';
  const direction = diffMinutes > 0 ? 'later' : 'earlier';

  return `${diffMinutes > 0 ? '+' : '-'}${magnitude} (${direction})`;
};

const AlternativeOfferDetails: React.FC<Props> = ({
  originalAllInRate,
  alternativeAllInRate,
  originalPickupScheduled,
  originalPickupLatest,
  alternativePickup,
  originalDeliveryScheduled,
  originalDeliveryLatest,
  alternativeDelivery,
}) => {
  const hasRateDifference =
    alternativeAllInRate !== null &&
    originalAllInRate !== null &&
    !numbersEqual(originalAllInRate, alternativeAllInRate);

  const hasPickupDifference =
    alternativePickup !== null &&
    originalPickupScheduled !== null &&
    !datesEqual(originalPickupScheduled, alternativePickup);

  const hasDeliveryDifference =
    alternativeDelivery !== null &&
    originalDeliveryScheduled !== null &&
    !datesEqual(originalDeliveryScheduled, alternativeDelivery);

  const rows: Array<{
    key: string;
    label: string;
    original: string;
    alternative: string;
    difference: string;
  }> = [];

  if (hasRateDifference) {
    rows.push({
      key: 'rate',
      label: 'All-In',
      original: formatCurrency(originalAllInRate),
      alternative: formatCurrency(alternativeAllInRate),
      difference: formatCurrencyDifference(originalAllInRate, alternativeAllInRate),
    });
  }

  if (hasPickupDifference) {
    rows.push({
      key: 'pickup',
      label: 'Pickup',
      original: dateWindowFormatter(originalPickupScheduled, originalPickupLatest ?? undefined),
      alternative: formatDateTime(alternativePickup),
      difference: formatTimeDifference(originalPickupScheduled, alternativePickup),
    });
  }

  if (hasDeliveryDifference) {
    rows.push({
      key: 'delivery',
      label: 'Delivery',
      original: dateWindowFormatter(originalDeliveryScheduled, originalDeliveryLatest ?? undefined),
      alternative: formatDateTime(alternativeDelivery),
      difference: formatTimeDifference(originalDeliveryScheduled, alternativeDelivery),
    });
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="row text-center mt-3">
      <div className="col-md-4 col-sm-12 mb-3">
        <h5>Offered</h5>
        {rows.map((row, index) => (
          <React.Fragment key={`${row.key}-original`}>
            <div className={index === 0 ? undefined : 'mt-2'}><strong>{row.label}</strong></div>
            <div>{row.original}</div>
          </React.Fragment>
        ))}
      </div>
      <div className="col-md-4 col-sm-12 mb-3">
        <h5>Alternative</h5>
        {rows.map((row, index) => (
          <React.Fragment key={`${row.key}-alternative`}>
            <div className={index === 0 ? undefined : 'mt-2'}><strong>{row.label}</strong></div>
            <div>{row.alternative}</div>
          </React.Fragment>
        ))}
      </div>
      <div className="col-md-4 col-sm-12 mb-3">
        <h5>Difference</h5>
        {rows.map((row, index) => (
          <React.Fragment key={`${row.key}-difference`}>
            <div className={index === 0 ? undefined : 'mt-2'}><strong>{row.label}</strong></div>
            <div>{row.difference}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default AlternativeOfferDetails;
